import { useCallback, useEffect, useRef, useState } from 'react'

import type {
  DriverIQUdfFieldInput,
  DriverIQUdfSlot,
  GetDriverIQUdfFields,
} from 'types/graphql'

import { useMutation, useQuery } from '@redwoodjs/web'

import {
  GET_DRIVER_IQ_UDF_FIELDS,
  SAVE_DRIVER_IQ_UDF_FIELDS,
} from 'src/graphql/driverIQUdfFields.graphql'

export const UDF_SLOTS: DriverIQUdfSlot[] = ['UDF1', 'UDF2', 'UDF3']

const MAX_LENGTH = 100

export interface UdfOptionState {
  /** Exact code sent to DriverIQ (e.g. BS-SP). Required. */
  code: string
  /** Optional name shown to recruiters. Blank shows the code. */
  name: string
}

export interface UdfSlotState {
  fieldName: string
  isRequired: boolean
  options: UdfOptionState[]
}

export type UdfRows = Record<DriverIQUdfSlot, UdfSlotState>
export type UdfErrors = Partial<Record<DriverIQUdfSlot, string>>

type UdfFields = GetDriverIQUdfFields['driverIQUdfFields'] | undefined

const emptyOption = (): UdfOptionState => ({ code: '', name: '' })

const emptySlot = (): UdfSlotState => ({
  fieldName: '',
  isRequired: false,
  options: [emptyOption()],
})

/** A slot is active iff it has a non-blank field name. */
export const isSlotActive = (row: UdfSlotState) => row.fieldName.trim() !== ''

export const buildRows = (fields: UdfFields): UdfRows => {
  const rows = {} as UdfRows
  for (const slot of UDF_SLOTS) {
    const field = fields?.find((f) => f.slot === slot)
    if (!field) {
      rows[slot] = emptySlot()
      continue
    }
    const options = [...field.options]
      .sort((a, b) => a.sortOrder - b.sortOrder)
      .map((o) => ({ code: o.value, name: o.label === o.value ? '' : o.label }))
    rows[slot] = {
      fieldName: field.label,
      isRequired: field.isRequired,
      options: options.length > 0 ? options : [emptyOption()],
    }
  }
  return rows
}

const isBlankOption = (o: UdfOptionState) => !o.code.trim() && !o.name.trim()

export const buildSaveInput = (rows: UdfRows): DriverIQUdfFieldInput[] =>
  UDF_SLOTS.filter((slot) => isSlotActive(rows[slot])).map((slot, index) => ({
    slot,
    label: rows[slot].fieldName.trim(),
    isRequired: rows[slot].isRequired,
    sortOrder: index,
    options: rows[slot].options
      .filter((o) => !isBlankOption(o))
      .map((o, optIndex) => ({
        // Display name is optional; the server falls back to the code.
        label: o.name.trim() || undefined,
        value: o.code.trim(),
        sortOrder: optIndex,
      })),
  }))

const findDuplicates = (items: string[]): string[] => {
  const seen = new Set<string>()
  const duplicates = new Set<string>()
  for (const item of items) {
    if (seen.has(item)) duplicates.add(item)
    seen.add(item)
  }
  return [...duplicates]
}

export const validateRows = (rows: UdfRows): UdfErrors => {
  const errors: UdfErrors = {}
  for (const slot of UDF_SLOTS) {
    const row = rows[slot]
    if (!isSlotActive(row)) continue

    if (row.fieldName.trim().length > MAX_LENGTH) {
      errors[slot] = `Field name must be ${MAX_LENGTH} characters or fewer`
      continue
    }

    const options = row.options.filter((o) => !isBlankOption(o))
    if (options.length === 0) {
      errors[slot] = 'Add at least one DriverIQ code'
      continue
    }
    if (options.some((o) => !o.code.trim())) {
      errors[slot] = 'Every option needs a DriverIQ code'
      continue
    }
    if (
      options.some(
        (o) =>
          o.code.trim().length > MAX_LENGTH || o.name.trim().length > MAX_LENGTH
      )
    ) {
      errors[slot] = `Codes and names must be ${MAX_LENGTH} characters or fewer`
      continue
    }

    // Codes are case-sensitive (they are sent verbatim to DriverIQ)
    const duplicateCodes = findDuplicates(options.map((o) => o.code.trim()))
    if (duplicateCodes.length > 0) {
      errors[slot] = `Duplicate DriverIQ code: ${duplicateCodes.join(', ')}`
      continue
    }

    // Non-blank names must be unique, case-insensitive
    const duplicateNames = findDuplicates(
      options.map((o) => o.name.trim().toLowerCase()).filter(Boolean)
    )
    if (duplicateNames.length > 0) {
      const originals = duplicateNames.map(
        (lower) =>
          options
            .find((o) => o.name.trim().toLowerCase() === lower)
            ?.name.trim() ?? lower
      )
      errors[slot] = `Duplicate name: ${originals.join(', ')}`
    }
  }
  return errors
}

const rowsEqual = (a: UdfRows, b: UdfRows) =>
  JSON.stringify(a) === JSON.stringify(b)

/**
 * Full-replace persist is unsafe until the query has hydrated. If the admin
 * opened Edit on the empty initial rows and the server later returns existing
 * slots, treating that empty form as a change would delete the company's UDF
 * config.
 */
export const canPersistUdfRows = (
  data: GetDriverIQUdfFields | undefined,
  rows: UdfRows,
  fields: UdfFields
): boolean => {
  if (!data) return false
  const emptyRows = buildRows(undefined)
  const serverRows = buildRows(fields)
  if (rowsEqual(rows, emptyRows) && !rowsEqual(serverRows, emptyRows)) {
    return false
  }
  return !rowsEqual(rows, serverRows)
}

interface UseDriverIQUdfFieldsEditorArgs {
  editing: boolean
}

export const useDriverIQUdfFieldsEditor = ({
  editing,
}: UseDriverIQUdfFieldsEditorArgs) => {
  const { data } = useQuery<GetDriverIQUdfFields>(GET_DRIVER_IQ_UDF_FIELDS)
  const fields = data?.driverIQUdfFields
  const fieldsLoaded = data !== undefined

  const [rows, setRows] = useState<UdfRows>(() => buildRows(fields))
  const [selectedSlot, setSelectedSlot] = useState<DriverIQUdfSlot>('UDF1')
  const [errors, setErrors] = useState<UdfErrors>({})
  const hasHydrated = useRef(fieldsLoaded)

  const [saveFields, { loading }] = useMutation(SAVE_DRIVER_IQ_UDF_FIELDS, {
    refetchQueries: [{ query: GET_DRIVER_IQ_UDF_FIELDS }],
    awaitRefetchQueries: true,
  })

  // Hydrate from the query in view mode, and once if Edit started before the
  // first response (otherwise the empty initial rows would look like a wipe).
  useEffect(() => {
    if (!fieldsLoaded) return
    if (editing && hasHydrated.current) return
    const next = buildRows(fields)
    setRows((prev) => (rowsEqual(prev, next) ? prev : next))
    hasHydrated.current = true
    if (!editing) {
      setSelectedSlot('UDF1')
      setErrors((prev) => (Object.keys(prev).length === 0 ? prev : {}))
    }
  }, [fields, editing, fieldsLoaded])

  const clearSlotError = (slot: DriverIQUdfSlot) => {
    setErrors((prev) => {
      if (!prev[slot]) return prev
      const next = { ...prev }
      delete next[slot]
      return next
    })
  }

  const updateSlot = (slot: DriverIQUdfSlot, patch: Partial<UdfSlotState>) => {
    setRows((prev) => ({ ...prev, [slot]: { ...prev[slot], ...patch } }))
    clearSlotError(slot)
  }

  const setFieldName = (slot: DriverIQUdfSlot, fieldName: string) => {
    setRows((prev) => {
      const row = prev[slot]
      // Deactivating a slot also drops its "required" flag
      const isRequired = fieldName.trim() ? row.isRequired : false
      return { ...prev, [slot]: { ...row, fieldName, isRequired } }
    })
    clearSlotError(slot)
  }

  const setRequired = (slot: DriverIQUdfSlot, isRequired: boolean) =>
    updateSlot(slot, { isRequired })

  const setOption = (
    slot: DriverIQUdfSlot,
    index: number,
    patch: Partial<UdfOptionState>
  ) => {
    setRows((prev) => ({
      ...prev,
      [slot]: {
        ...prev[slot],
        options: prev[slot].options.map((o, i) =>
          i === index ? { ...o, ...patch } : o
        ),
      },
    }))
    clearSlotError(slot)
  }

  const addOption = (slot: DriverIQUdfSlot) => {
    setRows((prev) => ({
      ...prev,
      [slot]: {
        ...prev[slot],
        options: [...prev[slot].options, emptyOption()],
      },
    }))
    clearSlotError(slot)
  }

  const removeOption = (slot: DriverIQUdfSlot, index: number) => {
    setRows((prev) => {
      if (prev[slot].options.length <= 1) return prev
      return {
        ...prev,
        [slot]: {
          ...prev[slot],
          options: prev[slot].options.filter((_, i) => i !== index),
        },
      }
    })
    clearSlotError(slot)
  }

  const validate = (): boolean => {
    const validation = validateRows(rows)
    setErrors(validation)
    const firstErrorSlot = UDF_SLOTS.find((slot) => validation[slot])
    if (firstErrorSlot) setSelectedSlot(firstErrorSlot)
    return !firstErrorSlot
  }

  /**
   * Full-replace save: only active slots are sent, so clearing a field name
   * deletes that slot server-side. No-op until the query has loaded (so a
   * missing response can never wipe existing configuration) and when nothing
   * changed (so editing credentials alone does not rewrite the options).
   */
  const save = async () => {
    if (!canPersistUdfRows(data, rows, fields)) return
    await saveFields({
      variables: { input: { fields: buildSaveInput(rows) } },
    })
  }

  const reset = useCallback(() => {
    setRows(buildRows(fields))
    setSelectedSlot('UDF1')
    setErrors({})
  }, [fields])

  return {
    rows,
    selectedSlot,
    selectSlot: setSelectedSlot,
    setFieldName,
    setRequired,
    setOption,
    addOption,
    removeOption,
    errors,
    validate,
    save,
    reset,
    loading,
    fieldsLoaded,
  }
}

export type DriverIQUdfFieldsEditor = ReturnType<
  typeof useDriverIQUdfFieldsEditor
>
